<script lang="ts">
  /**
   * The draft, both phases on one screen.
   *
   * Phase 1 fills the starting XI in batting order. Phase 2 picks two impact
   * subs — a pool, not a line-up, so the slot machinery is switched off rather
   * than reused. Which phase applies is the engine's call, read from
   * `draft.phase`; nothing here decides it.
   */
  import type { DraftPhase, Offer } from '@ipl-sim/engine-cricket';
  import type { Draft } from '../lib/draft.svelte';
  import type { Card } from '../lib/roster';
  import PlayerRow from '../components/PlayerRow.svelte';
  import { roleLabel } from '../lib/format';

  interface Props {
    draft: Draft;
    onnext: () => void;
    ontoast: (message: string) => void;
  }

  let { draft, onnext, ontoast }: Props = $props();

  const isXI = $derived(draft.phase === 'XI');

  /**
   * A pick can end a phase, and that has to be announced — the XI filling up
   * is what changes the meaning of tapping a card, and a silent switch from
   * "pick a position" to "pick a sub" reads as the screen breaking.
   *
   * `before` and `after` are annotated rather than inferred on purpose. TypeScript
   * narrows `draft.phase` through an aliased comparison and has no idea the
   * intervening call changed it, which quietly turned the second check into a
   * comparison it believed could never be true.
   */
  function announce(before: DraftPhase): void {
    const after: DraftPhase = draft.phase;
    if (before === after) return;
    if (after === 'IMPACT') ontoast('Starting XI complete — now pick 2 impact subs');
    else if (after === 'DONE') ontoast('Impact subs chosen — name your team');
  }

  function choose(offer: Offer): void {
    const before: DraftPhase = draft.phase;
    draft.choose(offer);
    announce(before);
  }

  function assign(player: Card, slot: number): void {
    const before: DraftPhase = draft.phase;
    draft.assign(player, slot);
    announce(before);
  }

  function respin(): void {
    if (!draft.respin()) ontoast('No re-spins left');
  }
</script>

<section class="screen active" aria-label="Draft">
  <div class="draft-top">
    <div class="draft-head">
      <div>
        <h2>{isXI ? 'Starting XI' : 'Impact subs'}</h2>
        <div class="reqs">
          {#each draft.requirements as req (req.label)}
            <span class={req.met ? 'ok' : 'no'}>{req.label}</span>
          {/each}
        </div>
      </div>
      <div>
        <div class="count tabular"><b>{draft.count.have}</b>/{draft.count.need}</div>
        <!--
          Never disabled when the squad on screen is a dead end: moving on from
          one of those is free, so the button must stay live even at zero.
        -->
        <button
          class="btn-sm btn-secondary"
          style="margin-top:5px"
          disabled={draft.respinsLeft === 0 && draft.hasPickable}
          onclick={respin}
        >
          <svg
            style="width:11px;height:11px;stroke:currentColor;fill:none;stroke-width:2;vertical-align:-1px"
            aria-hidden="true"><use href="#i-refresh" /></svg
          >
          {draft.hasPickable ? `Re-spin · ${draft.respinsLeft}` : 'Next squad'}
        </button>
      </div>
    </div>

    {#if isXI}
      <ol class="xi" aria-label="Your starting XI">
        {#each draft.xi as row (row.slot)}
          <li class:filled={row.player} class:legal={row.isLegalTarget} data-role={row.player?.role}>
            <span class="s">{row.slot}</span>
            {#if row.player}
              <span class="who">
                <span class="nmtxt">{row.player.name}</span>
                {#if !row.player.isIndian}
                  <svg class="plane" aria-hidden="true"><use href="#i-plane" /></svg>
                  <span class="sr">Overseas</span>
                {/if}
                <span class="from">{row.player.from}</span>
              </span>
              <span class="mini">{roleLabel(row.player.role)}</span>
            {:else}
              <span class="empty">—</span>
              <span></span>
            {/if}
          </li>
        {/each}
      </ol>
    {:else}
      <ul class="subs" aria-label="Your impact subs">
        {#each draft.subs as sub, i (i)}
          {#if sub}
            <li data-role={sub.role}>
              <span class="who">{sub.name}</span>
              <span class="from">{sub.from}</span>
              <span class="mini tagrole">{roleLabel(sub.role)}</span>
            </li>
          {:else}
            <li class="empty"><span>Not chosen yet</span></li>
          {/if}
        {/each}
      </ul>
    {/if}
  </div>

  <!--
    Only shown when a player has more than one legal position. A single legal
    position assigns immediately — asking "where?" when there is one answer is
    a tap for nothing. Never appears in the impact phase, which has no order.
  -->
  {#if draft.selected}
    {@const selected = draft.selected}
    <div class="slotbar">
      <div class="lbl">Where does <b>{selected.name}</b> bat?</div>
      <div class="chips">
        {#each draft.offers.find((o) => o.player.id === selected.id)?.legalSlots ?? [] as slot (slot)}
          <button onclick={() => assign(selected, slot)}>{slot}</button>
        {/each}
        <button class="cancel" onclick={() => draft.cancelPick()}>Cancel</button>
      </div>
    </div>
  {/if}

  <div class="squad-panel">
    {#if !isXI}
      <div class="phase-banner">
        <svg aria-hidden="true"><use href="#i-swap" /></svg>
        <p>
          Your XI is set. Now pick <b>two Indian players</b> as impact subs — any role, any season.
          One can come on mid-match.
        </p>
      </div>
    {/if}

    <div class="squad-head">
      <span class="squad-name">{draft.squad.city}</span>
      <span class="era">{draft.squad.year}</span>
    </div>
    <p class="faint" style="margin:0 0 var(--space-3)">
      {draft.squad.squad.length} players ·
      {isXI ? 'sorted by batting order · greyed = no free position' : 'greyed = overseas or already picked'}
    </p>

    {#each draft.rows as offer (offer.player.id)}
      <PlayerRow
        {offer}
        showRange={isXI}
        selected={draft.selected?.id === offer.player.id}
        onchoose={choose}
      />
    {/each}
  </div>

  {#if draft.isComplete}
    <div class="actions">
      <button class="btn-primary" onclick={onnext}>Name your team</button>
    </div>
  {/if}
</section>
